import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: {
      main: string;
      light: string;
      dark: string;
    };
  }

  interface PaletteOptions {
    neutral?: {
      main: string;
      light: string;
      dark: string;
    };
  }

  interface Theme {
    radii: {
      small: number;
      medium: number;
      large: number;
      pill: number;
    };
  }

  // allow configuration of radii in createTheme
  interface ThemeOptions {
    radii?: {
      small?: number;
      medium?: number;
      large?: number;
      pill?: number;
    };
  }
}

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3B82F6',
      light: '#60A5FA',
      dark: '#2563EB',
      contrastText: '#F8FAFC',
    },
    secondary: {
      main: '#64748B',
      light: '#94A3B8',
      dark: '#475569',
      contrastText: '#F8FAFC',
    },
    success: {
      main: '#22C55E',
      light: '#4ADE80',
      dark: '#16A34A',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
    },
    info: {
      main: '#3B82F6',
      light: '#60A5FA',
      dark: '#2563EB',
    },
    neutral: {
      main: '#94A3B8',
      light: '#B6C0CC',
      dark: '#6B7280',
    },
    background: {
      default: '#0A1018',
      paper: '#111A25',
    },
    text: {
      primary: '#F8FAFC',
      secondary: '#94A3B8',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: 'clamp(2rem, 5vw, 2.5rem)',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontSize: 'clamp(1.75rem, 4vw, 2rem)',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: 'clamp(1.5rem, 3.5vw, 1.75rem)',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
    },
    h4: {
      fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: 'clamp(1rem, 2vw, 1.125rem)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 10,
  },
  // Radii scale for consistent corner sizes
  radii: {
    small: 6,
    medium: 10,
    large: 14,
    pill: 999,
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          padding: '10px 20px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(15, 23, 42, 0.35)',
          },
          [theme.breakpoints.down('sm')]: {
            padding: '8px 16px',
            fontSize: '0.875rem',
          },
        }),
        contained: {
          '&:hover': {
            boxShadow: '0 10px 24px rgba(37, 99, 235, 0.28)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape?.borderRadius ?? 10,
          boxShadow: '0 8px 30px rgba(2,6,23,0.6)',
          border: '1px solid rgba(255,255,255,0.04)',
          backgroundColor: theme.palette.background.paper,
          WebkitBackgroundClip: 'padding-box',
          backgroundClip: 'padding-box',
          '&:hover': {
            boxShadow: '0 14px 40px rgba(2,6,23,0.7)',
          },
          transition: 'all 0.22s ease-in-out',
          [theme.breakpoints.down('sm')]: {
            borderRadius: 12,
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
        },
        elevation3: {
          boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            background: '#0E1722',
            color: '#F8FAFC',
            '& fieldset': {
              borderColor: 'rgba(255,255,255,0.06)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255,255,255,0.12)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#3B82F6',
              borderWidth: 2,
            },
            '&.Mui-focused': {
              boxShadow: '0 8px 24px rgba(59,130,246,0.18)',
            },
            '& input': { color: '#F8FAFC' },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: 'transparent',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          color: '#E6EEF2',
        },
        body: {
          borderBottom: '1px solid rgba(255,255,255,0.04)',
          color: '#D1D7DE',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRight: '1px solid rgba(255,255,255,0.04)',
          backgroundColor: theme?.palette?.background?.default || '#0B0F14',
          WebkitBackgroundClip: 'padding-box',
          backgroundClip: 'padding-box',
        }),
      },
      defaultProps: {
        // Make drawer full screen on mobile
        PaperProps: {
          sx: {
            '@media (max-width: 600px)': {
              width: '100%',
            },
          },
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        // Make dialogs full screen on mobile
        fullScreen: false,
      },
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: '#0B0F14',
          color: '#fff',
          borderRadius: theme.shape?.borderRadius ?? 10,
          border: '1px solid rgba(255,255,255,0.04)',
          [theme.breakpoints.down('sm')]: {
            margin: 16,
            width: 'calc(100% - 32px)',
            maxWidth: 'none',
          },
        }),
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            paddingLeft: 16,
            paddingRight: 16,
          },
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#091321',
          color: '#E2E8F0',
          boxShadow: '0 6px 18px rgba(2,6,23,0.6)',
          borderBottom: '1px solid rgba(255,255,255,0.04)',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: ({ theme }: any) => ({
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          margin: 0,
          minHeight: '100vh',
        }),
        '#root': {
          minHeight: '100vh',
          backgroundColor: 'inherit',
        },
        html: {
          height: '100%'
        }
      },
    },
  },
});

export default theme; 